"use client";

import SharedComponents, { Pagination } from "@audacia-hq/shared/components";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useLocale } from "../../context/LocaleContext";
import { GET_RATING_LIST } from "../../graphql/query";
import { useLazyFetcher } from "../../hooks/useClientFetcher";
import { Expert, ExpertIdentifierMap } from "../../models/expert";
import { Rating, RatingList, RatingSummary } from "../../models/ratings";
import SidePanelIcon from "../../public/assets/reviews/side_panel_icon.svg";

import RatingCard from "./RatingCard";
import RatingsFilter from "./RatingsFilter";

const initialRatingSummary: RatingSummary[] = [
  { count: 0, rate: 5, percent: 0 },
  { count: 0, rate: 4, percent: 0 },
  { count: 0, rate: 3, percent: 0 },
  { count: 0, rate: 2, percent: 0 },
  { count: 0, rate: 1, percent: 0 },
];

interface Props {
  pageSize?: number;
  expert?: Expert;
  expertIdentifiers?: ExpertIdentifierMap;
  defaultRatings: RatingList;
  ratingSummaryList: RatingSummary[]
}

const RatingsList: React.FC<Props> = ({
  expert, expertIdentifiers, pageSize = 10, defaultRatings, ratingSummaryList,
}) => {
  const { t } = useTranslation("common");
  const locale = useLocale();
  const [getRatingList, { data: ratingsData, loading }] = useLazyFetcher<{ ratings: RatingList }>(GET_RATING_LIST);
  const [currentPage, setCurrentPage] = useState<{ page: number, rate?: number }>({ page: 1, rate: undefined });
  const [totalRows, setTotalRows] = useState(defaultRatings.totalRows);
  const [showHint, setShowHint] = useState(!expert);
  const [ratings, setRatings] = useState<Rating[]>(defaultRatings.data);
  const [ratingsSummary, setRatingsSummary] = useState<RatingSummary[]>(initialRatingSummary);
  const [totalSummary, setTotalSummary] = useState<string>("0");
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    getRatingList({
      variables: {
        filters: {
          context: "idealvoyance",
          offset: pageSize * (currentPage.page - 1),
          limit: pageSize,
          rate: currentPage.rate,
          language: locale.toUpperCase(),
          expertUid: expert?.expertUid,
        },
      },
    });
  }, [currentPage, expert]);

  useEffect(() => {
    if (!ratingsData) return;
    setTotalRows(ratingsData?.ratings.totalRows || 0);
    setRatings(ratingsData?.ratings.data || []);
  }, [ratingsData]);

  useEffect(() => {
    if (!ratingSummaryList) return;
    const total = ratingSummaryList.reduce((acc, curr) => acc + curr.count, 0);
    setTotalSummary(total.toLocaleString().replace(/\s/g, "'"));
    setRatingsSummary([...initialRatingSummary
      .map((irs) => ratingSummaryList.find((rsd) => irs.rate === rsd.rate) || irs)
      .map((rs) => ({ ...rs, percent: Math.round((rs.count / total) * 10000) / 100 })),
    ]);
  }, [ratingSummaryList]);

  const addOrRemoveRatingFilter = (rate: number) => {
    if (currentPage.rate === rate) return setCurrentPage({ ...currentPage, rate: undefined, page: 1 });
    return setCurrentPage({ ...currentPage, rate, page: 1 });
  };

  const changePage = (page: number) => {
    document.getElementById("latest-reviews").scrollIntoView();
    setCurrentPage((p) => ({ ...p, page }));
  };

  return (
    ratings?.length > 0
      ? (
        <div
          id="latest-reviews"
          className={`flex flex-col py-10 md:py-10 lg:py-20 gap-8 w-full ${expert ? "" : "px-4 md:px-6 lg:px-16 lg:flex-row"}`}
        >
          <div className="flex flex-col gap-6">
            {showHint && (
              <div className="relative flex flex-col rounded-xl border border-[#D2D2D2] p-6 gap-5 bg-[#F7F9FC] lg:w-[399px]">
                <button type="button" className="absolute top-2 right-3 text-[#475569]" onClick={() => setShowHint(false)}>
                  <FontAwesomeIcon icon={faClose} />
                </button>
                <Image className="m-auto" src={SidePanelIcon} alt="RatingsPanel" />
                <span className="text-[#2B2B2B] font-normal text-base">{t("latestReviewsHint")}</span>
                <Link
                  href="/"
                  className="px-4 py-3 border border-[#2563EB] font-normal text-base text-[#2563EB] rounded-md"
                >
                  <button className="w-full" type="button">{t("consultationCTA")}</button>
                </Link>
              </div>
            )}
            <div className={`space-y-6 hidden ${expert ? "" : "lg:block"}`}>
              <RatingsFilter
                expert={expert}
                ratingsSummary={ratingsSummary}
                onClick={addOrRemoveRatingFilter}
                currentRate={currentPage.rate}
                total={totalSummary}
              />
            </div>
          </div>
          <div className="flex flex-col overflow-auto w-full">
            {!expert && <span className="py-6 text-3xl">{t("menu.customerReviews")}</span>}
            <div className={`space-y-6 ${expert ? "" : "lg:hidden"}`}>
              <RatingsFilter
                expert={expert}
                ratingsSummary={ratingsSummary}
                onClick={addOrRemoveRatingFilter}
                currentRate={currentPage.rate}
                total={totalSummary}
              />
            </div>
            {loading ? <span className="w-full mx-auto"><SharedComponents.Spinner /></span>
              : [...ratings]
                .map((rating) => (
                  <RatingCard
                    className="w-full break-words"
                    key={rating.ratingUid}
                    rating={rating}
                    expert={expertIdentifiers?.[rating.expertUid] || expert}
                    expertLink={!expert}
                    showExpert={!expert}
                    showAnswer
                    showUser
                  />
                ))}
            <div className={`flex w-full pt-5 ${(loading || totalRows <= ratings.length) ? "hidden" : ""} ${expert ? "justify-center" : "justify-end"}`}>
              <Pagination
                currentPage={currentPage.page}
                goToNext={() => changePage(currentPage.page + 1)}
                goToPage={changePage}
                goToPrevious={() => changePage(currentPage.page - 1)}
                pageCount={Math.ceil(totalRows / pageSize)}
              />
            </div>
          </div>
        </div>
      ) : <></>
  );
};

export default RatingsList;
